import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  makeStyles,
  Paper,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Avatar,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Button,
  IconButton,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import { format, parseISO, isSameDay, isToday, isYesterday } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../services/api";

const COLORS = [
  "#1976d2", "#388e3c", "#d32f2f", "#7b1fa2", "#f57c00",
  "#0288d1", "#c2185b", "#5d4037", "#455a64", "#00796b",
];
const colorFor = (id) => COLORS[(Number(id) || 0) % COLORS.length];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    width: "100%",
    minHeight: 0,
    padding: theme.spacing(1),
    gap: theme.spacing(1),
    background: "#eef2f5",
    boxSizing: "border-box",
  },
  sidebar: {
    width: 360,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    minHeight: 0,
  },
  chatsList: { flex: 1, overflowY: "auto" },
  messagesPane: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: 0,
    overflow: "hidden",
    minHeight: 0,
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2, 3),
    background:
      "linear-gradient(180deg, #e9eef3 0%, #dde4eb 100%)",
  },
  header: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: "#f5f7fa",
    display: "flex",
    flexDirection: "column",
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  headerAvatar: {
    width: 44,
    height: 44,
    fontWeight: 600,
    color: "#fff",
  },
  dateDivider: {
    display: "flex",
    justifyContent: "center",
    margin: theme.spacing(2, 0, 1),
  },
  dateChip: {
    background: "rgba(255,255,255,0.85)",
    color: "#555",
    fontSize: 12,
    padding: "4px 10px",
    borderRadius: 12,
    boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
  },
  row: {
    display: "flex",
    marginBottom: 2,
    alignItems: "flex-end",
    gap: 6,
  },
  rowMine: { justifyContent: "flex-end" },
  rowOther: { justifyContent: "flex-start" },
  avatarGutter: { width: 32, display: "flex", justifyContent: "center" },
  smallAvatar: { width: 28, height: 28, fontSize: 12, color: "#fff" },
  bubble: {
    maxWidth: "65%",
    padding: "6px 10px 4px",
    borderRadius: 10,
    boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    position: "relative",
    fontSize: 14,
    lineHeight: 1.35,
  },
  bubbleMine: {
    background: "#d9fdd3",
    borderTopRightRadius: 2,
  },
  bubbleOther: {
    background: "#ffffff",
    borderTopLeftRadius: 2,
  },
  bubbleFirstOfGroup: {
    marginTop: 8,
  },
  senderName: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 2,
  },
  timeStamp: {
    fontSize: 10,
    color: "#667781",
    float: "right",
    marginLeft: 8,
    marginTop: 4,
    userSelect: "none",
  },
  replyBox: {
    borderLeft: "3px solid #06cf9c",
    background: "rgba(6,207,156,0.08)",
    padding: "4px 8px",
    borderRadius: 4,
    marginBottom: 4,
    fontSize: 12,
    color: "#444",
  },
  deletedBubble: {
    fontStyle: "italic",
    color: "#888",
  },
  empty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: theme.palette.text.secondary,
    flexDirection: "column",
    gap: 8,
  },
  badge: { marginLeft: theme.spacing(1) },
  filterRow: { display: "flex", gap: 8, marginBottom: 8 },
}));

const formatDate = (iso) => {
  try {
    return format(parseISO(iso), "dd/MM/yyyy HH:mm");
  } catch {
    return iso || "";
  }
};

const formatTime = (iso) => {
  try {
    return format(parseISO(iso), "HH:mm");
  } catch {
    return "";
  }
};

const formatDayLabel = (iso) => {
  try {
    const d = parseISO(iso);
    if (isToday(d)) return "Hoje";
    if (isYesterday(d)) return "Ontem";
    return format(d, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return "";
  }
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";

const ChatsView = ({ token }) => {
  const classes = useStyles();
  const cfg = useMemo(
    () => ({ headers: { "X-Ownerview-Token": token || "" } }),
    [token]
  );

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [search, setSearch] = useState("");
  const [chatType, setChatType] = useState("all");
  const [participant, setParticipant] = useState("");
  const [messageContent, setMessageContent] = useState("");

  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);

  const [messages, setMessages] = useState([]);
  const [chatDetail, setChatDetail] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesAreaRef = useRef(null);

  const [revealedDeleted, setRevealedDeleted] = useState({});
  const toggleRevealDeleted = (id) =>
    setRevealedDeleted((prev) => ({ ...prev, [id]: !prev[id] }));

  // Busca de mensagens (escopada aos chats internos)
  const [searchOpen, setSearchOpen] = useState(false);
  const [msgQuery, setMsgQuery] = useState("");
  const [msgSender, setMsgSender] = useState("");
  const [msgDateFrom, setMsgDateFrom] = useState("");
  const [msgDateTo, setMsgDateTo] = useState("");
  const [msgResults, setMsgResults] = useState([]);
  const [msgSearching, setMsgSearching] = useState(false);
  const [msgHasMore, setMsgHasMore] = useState(false);
  const [msgPage, setMsgPage] = useState(1);
  const [msgCount, setMsgCount] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/internal-chat-view/companies", cfg);
        if (active) setCompanies(data);
      } catch (err) {
        if (
          err?.response?.status === 404 ||
          err?.response?.status === 403
        ) {
          setForbidden(true);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [cfg]);

  const loadChats = useCallback(async () => {
    setChatsLoading(true);
    try {
      const { data } = await api.get("/internal-chat-view/chats", {
        ...cfg,
        params: {
          companyId: companyId || undefined,
          searchParam: search || undefined,
          chatType: chatType !== "all" ? chatType : undefined,
          participant: participant || undefined,
          messageContent: messageContent || undefined,
          pageNumber: 1,
        },
      });
      setChats(data.records || []);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) {
        setForbidden(true);
      }
    } finally {
      setChatsLoading(false);
    }
  }, [companyId, search, chatType, participant, messageContent, cfg]);

  useEffect(() => {
    const t = setTimeout(loadChats, 300);
    return () => clearTimeout(t);
  }, [loadChats]);

  useEffect(() => {
    if (!selectedChat) {
      setMessages([]);
      setChatDetail(null);
      setPageNumber(1);
      setHasMore(false);
      return;
    }
    let active = true;
    (async () => {
      setMessagesLoading(true);
      try {
        const { data } = await api.get(
          `/internal-chat-view/chats/${selectedChat.id}/messages`,
          { ...cfg, params: { pageNumber: 1 } }
        );
        if (!active) return;
        setMessages(data.records || []);
        setChatDetail(data.chat || null);
        setPageNumber(1);
        setHasMore(!!data.hasMore);
        // rola para o final (mensagens mais recentes)
        setTimeout(() => {
          const el = messagesAreaRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 0);
      } finally {
        if (active) setMessagesLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedChat, cfg]);

  const loadMore = useCallback(async () => {
    if (!selectedChat || !hasMore || loadingMore) return;
    const el = messagesAreaRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    setLoadingMore(true);
    try {
      const next = pageNumber + 1;
      const { data } = await api.get(
        `/internal-chat-view/chats/${selectedChat.id}/messages`,
        { ...cfg, params: { pageNumber: next } }
      );
      // data.records vem em ordem crescente; concatenamos as mais antigas no topo
      setMessages((prev) => [...(data.records || []), ...prev]);
      setPageNumber(next);
      setHasMore(!!data.hasMore);
      // mantém a posição visual após prepend
      setTimeout(() => {
        const el2 = messagesAreaRef.current;
        if (el2) el2.scrollTop = el2.scrollHeight - prevHeight;
      }, 0);
    } finally {
      setLoadingMore(false);
    }
  }, [selectedChat, hasMore, loadingMore, pageNumber, cfg]);

  const handleMessagesScroll = useCallback(
    (e) => {
      if (e.currentTarget.scrollTop < 80) {
        loadMore();
      }
    },
    [loadMore]
  );

  const runMessageSearch = useCallback(
    async (page = 1) => {
      setMsgSearching(true);
      try {
        const { data } = await api.get("/internal-chat-view/messages", {
          ...cfg,
          params: {
            content: msgQuery || undefined,
            sender: msgSender || undefined,
            companyId: companyId || undefined,
            chatType: chatType !== "all" ? chatType : undefined,
            dateFrom: msgDateFrom || undefined,
            dateTo: msgDateTo || undefined,
            pageNumber: page,
          },
        });
        const records = data.records || [];
        setMsgResults((prev) => (page === 1 ? records : [...prev, ...records]));
        setMsgHasMore(!!data.hasMore);
        setMsgCount(data.count || 0);
        setMsgPage(page);
      } finally {
        setMsgSearching(false);
      }
    },
    [cfg, msgQuery, msgSender, companyId, chatType, msgDateFrom, msgDateTo]
  );

  const openChatFromResult = (m) => {
    if (!m?.chat) return;
    setSelectedChat({ id: m.chat.id, ...m.chat });
    setSearchOpen(false);
  };

  const chatTitle = (chat) => {
    if (chat.isGroup) return chat.groupName || chat.title || `Grupo #${chat.id}`;
    const members = (chat.users || [])
      .map((cu) => cu.user?.name)
      .filter(Boolean)
      .join(" / ");
    return members || chat.title || `Chat #${chat.id}`;
  };

  const headerInfo = useMemo(() => {
    if (!chatDetail) return null;
    const participants = (chatDetail.users || [])
      .map((cu) => cu.user)
      .filter(Boolean);
    return {
      title: chatTitle(chatDetail),
      company: chatDetail.company?.name,
      isGroup: chatDetail.isGroup,
      participants,
      ownerId: chatDetail.ownerId,
      groupAdminId: chatDetail.groupAdminId,
    };
  }, [chatDetail]);

  if (forbidden) {
    return (
      <div className={classes.empty}>
        <Typography variant="h6">Not Found</Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.sidebar} elevation={1}>
        <div className={classes.filterRow}>
          <FormControl fullWidth size="small">
            <InputLabel>Empresa</InputLabel>
            <Select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              label="Empresa"
            >
              <MenuItem value="">Todas</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} ({c.totalChats})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" style={{ minWidth: 110 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={chatType}
              onChange={(e) => setChatType(e.target.value)}
              label="Tipo"
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="group">Grupos</MenuItem>
              <MenuItem value="private">Privados</MenuItem>
            </Select>
          </FormControl>
        </div>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Buscar título/grupo"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <TextField
          size="small"
          variant="outlined"
          placeholder="Participante (nome/email)"
          value={participant}
          onChange={(e) => setParticipant(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <TextField
          size="small"
          variant="outlined"
          placeholder="Texto em alguma mensagem"
          value={messageContent}
          onChange={(e) => setMessageContent(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <Divider />
        <div className={classes.chatsList}>
          {chatsLoading && <CircularProgress size={20} style={{ margin: 8 }} />}
          <List dense>
            {chats.map((chat) => (
              <ListItem
                key={chat.id}
                button
                selected={selectedChat?.id === chat.id}
                onClick={() => setSelectedChat(chat)}
              >
                <Avatar style={{ marginRight: 8 }}>
                  {chat.isGroup ? "G" : "C"}
                </Avatar>
                <ListItemText
                  primary={
                    <span>
                      {chatTitle(chat)}
                      {chat.company?.name && (
                        <Chip
                          size="small"
                          label={chat.company.name}
                          className={classes.badge}
                        />
                      )}
                    </span>
                  }
                  secondary={
                    <>
                      {chat.lastMessage || "(sem mensagens)"}
                      <br />
                      <small>{formatDate(chat.updatedAt)}</small>
                    </>
                  }
                />
              </ListItem>
            ))}
            {!chatsLoading && chats.length === 0 && (
              <ListItem>
                <ListItemText primary="Nenhum chat" />
              </ListItem>
            )}
          </List>
        </div>
        <Divider />
        <Button
          fullWidth
          size="small"
          startIcon={<SearchIcon />}
          onClick={() => setSearchOpen((v) => !v)}
          style={{ marginTop: 8 }}
          variant={searchOpen ? "contained" : "outlined"}
          color="primary"
        >
          {searchOpen ? "Fechar busca de mensagens" : "Buscar em mensagens"}
        </Button>
      </Paper>

      <Paper className={classes.messagesPane} elevation={1}>
        {searchOpen ? (
          <>
            <div className={classes.header} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <Typography variant="subtitle1">
                  Buscar mensagens nos chats internos
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {msgCount > 0 ? `${msgCount} resultado(s)` : "Filtros aplicam tipo/empresa da barra lateral."}
                </Typography>
              </div>
              <IconButton size="small" onClick={() => setSearchOpen(false)}>
                <CloseIcon />
              </IconButton>
            </div>
            <div style={{ padding: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <TextField
                label="Conteúdo"
                size="small"
                variant="outlined"
                value={msgQuery}
                onChange={(e) => setMsgQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runMessageSearch(1)}
                style={{ flex: 2, minWidth: 200 }}
              />
              <TextField
                label="Remetente (nome/email)"
                size="small"
                variant="outlined"
                value={msgSender}
                onChange={(e) => setMsgSender(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runMessageSearch(1)}
                style={{ flex: 1, minWidth: 160 }}
              />
              <TextField
                label="De"
                type="date"
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={msgDateFrom}
                onChange={(e) => setMsgDateFrom(e.target.value)}
              />
              <TextField
                label="Até"
                type="date"
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={msgDateTo}
                onChange={(e) => setMsgDateTo(e.target.value)}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => runMessageSearch(1)}
                disabled={msgSearching}
              >
                Buscar
              </Button>
            </div>
            <div className={classes.messagesArea}>
              {msgSearching && msgPage === 1 && <CircularProgress size={20} />}
              {!msgSearching && msgResults.length === 0 && (
                <Typography color="textSecondary">
                  Preencha os filtros e clique em Buscar.
                </Typography>
              )}
              {msgResults.map((m) => (
                <Paper
                  key={m.id}
                  style={{
                    padding: 8,
                    marginBottom: 8,
                    borderLeft: "3px solid #3f51b5",
                    cursor: "pointer",
                  }}
                  onClick={() => openChatFromResult(m)}
                  title="Abrir este chat"
                >
                  <div style={{ fontSize: 12, color: "#888" }}>
                    <strong>{m.sender?.name || "?"}</strong>
                    {" • "}
                    {formatDate(m.createdAt)}
                    {m.isEdited ? " • (editada)" : ""}
                    {m.isDeleted ? " • (apagada)" : ""}
                  </div>
                  <div>
                    {m.message ||
                      (m.mediaName ? `[mídia: ${m.mediaName}]` : "")}
                  </div>
                  <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                    {m.chat?.company?.name && (
                      <Chip size="small" label={m.chat.company.name} />
                    )}
                    <Chip
                      size="small"
                      color={m.chat?.isGroup ? "primary" : "default"}
                      label={
                        m.chat?.isGroup
                          ? m.chat?.groupName || `Grupo #${m.chat?.id}`
                          : m.chat?.title || `Chat #${m.chat?.id}`
                      }
                    />
                  </div>
                </Paper>
              ))}
              {msgHasMore && (
                <div style={{ textAlign: "center", padding: 8 }}>
                  <Button
                    size="small"
                    onClick={() => runMessageSearch(msgPage + 1)}
                    disabled={msgSearching}
                  >
                    {msgSearching ? "Carregando..." : "Carregar mais"}
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : !selectedChat ? (
          <div className={classes.empty}>
            <Typography>Selecione um item.</Typography>
          </div>
        ) : (
          <>
            <div className={classes.header}>
              <div className={classes.headerTop}>
                <Avatar
                  className={classes.headerAvatar}
                  style={{ background: colorFor(chatDetail?.id) }}
                >
                  {chatDetail?.isGroup
                    ? "G"
                    : initials(headerInfo?.title || "?")}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" noWrap>
                    {headerInfo?.title || "..."}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {headerInfo?.company}
                    {headerInfo?.participants?.length
                      ? ` • ${headerInfo.participants.length} participante(s)`
                      : ""}
                  </Typography>
                </div>
              </div>
              {headerInfo?.participants?.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    marginTop: 8,
                  }}
                >
                  {headerInfo.participants.map((u) => {
                    const isAdmin =
                      headerInfo.isGroup && u.id === headerInfo.groupAdminId;
                    const isOwner = u.id === headerInfo.ownerId;
                    const tag = isAdmin
                      ? " (admin)"
                      : isOwner && !headerInfo.isGroup
                      ? " (criador)"
                      : "";
                    return (
                      <Chip
                        key={u.id}
                        size="small"
                        avatar={
                          <Avatar style={{ background: colorFor(u.id), color: "#fff" }}>
                            {initials(u.name)}
                          </Avatar>
                        }
                        label={`${u.name}${tag}`}
                        title={u.email || ""}
                        color={isAdmin ? "primary" : "default"}
                      />
                    );
                  })}
                </div>
              )}
            </div>
            <div
              className={classes.messagesArea}
              ref={messagesAreaRef}
              onScroll={handleMessagesScroll}
            >
              {loadingMore && (
                <div style={{ textAlign: "center", padding: 8 }}>
                  <CircularProgress size={18} />
                </div>
              )}
              {!hasMore && !messagesLoading && messages.length > 0 && (
                <div className={classes.dateDivider}>
                  <span className={classes.dateChip}>início do histórico</span>
                </div>
              )}
              {messagesLoading && <CircularProgress size={20} />}
              {messages.map((m, idx) => {
                const prev = idx > 0 ? messages[idx - 1] : null;
                const newDay =
                  !prev ||
                  !isSameDay(parseISO(m.createdAt), parseISO(prev.createdAt));
                const sameSenderAsPrev =
                  prev &&
                  !newDay &&
                  prev.senderId === m.senderId;
                const isMine =
                  !chatDetail?.isGroup &&
                  m.senderId === chatDetail?.ownerId;
                const showAvatar =
                  !isMine && (!sameSenderAsPrev || newDay);
                const showName = !sameSenderAsPrev || newDay;

                return (
                  <React.Fragment key={m.id}>
                    {newDay && (
                      <div className={classes.dateDivider}>
                        <span className={classes.dateChip}>
                          {formatDayLabel(m.createdAt)}
                        </span>
                      </div>
                    )}
                    <div
                      className={`${classes.row} ${
                        isMine ? classes.rowMine : classes.rowOther
                      }`}
                    >
                      {!isMine && (
                        <div className={classes.avatarGutter}>
                          {showAvatar ? (
                            <Avatar
                              className={classes.smallAvatar}
                              style={{ background: colorFor(m.senderId) }}
                              title={m.sender?.name || ""}
                            >
                              {initials(m.sender?.name)}
                            </Avatar>
                          ) : null}
                        </div>
                      )}
                      <div
                        className={`${classes.bubble} ${
                          isMine ? classes.bubbleMine : classes.bubbleOther
                        } ${!sameSenderAsPrev ? classes.bubbleFirstOfGroup : ""}`}
                      >
                        {showName && (
                          <div
                            className={classes.senderName}
                            style={{ color: colorFor(m.senderId) }}
                          >
                            {m.sender?.name || "Desconhecido"}
                          </div>
                        )}
                        {m.replyTo && (
                          <div className={classes.replyBox}>
                            <strong>{m.replyTo.sender?.name || "?"}</strong>
                            <div
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 280,
                              }}
                            >
                              {m.replyTo.message || "(mídia)"}
                            </div>
                          </div>
                        )}
                        {m.isDeleted && !revealedDeleted[m.id] ? (
                          <div className={classes.deletedBubble}>
                            🚫 Mensagem deletada
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => toggleRevealDeleted(m.id)}
                              style={{
                                marginLeft: 6,
                                padding: 0,
                                textTransform: "none",
                                fontStyle: "normal",
                              }}
                            >
                              Ver
                            </Button>
                          </div>
                        ) : (
                          <>
                            {m.isDeleted && (
                              <div
                                style={{
                                  fontSize: 10,
                                  color: "#c62828",
                                  marginBottom: 4,
                                }}
                              >
                                ⚠ Conteúdo de mensagem deletada
                              </div>
                            )}
                            <div>
                              {m.message ||
                                (m.mediaName
                                  ? `[mídia: ${m.mediaName}]`
                                  : "")}
                            </div>
                            {m.mediaPath && (
                              <div style={{ marginTop: 4 }}>
                                <a
                                  href={m.mediaPath}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {m.mediaName || "abrir mídia"}
                                </a>{" "}
                                <small>({m.mediaType})</small>
                              </div>
                            )}
                            {m.isDeleted && (
                              <Button
                                size="small"
                                onClick={() => toggleRevealDeleted(m.id)}
                                style={{
                                  padding: 0,
                                  textTransform: "none",
                                  marginTop: 2,
                                }}
                              >
                                ocultar
                              </Button>
                            )}
                          </>
                        )}
                        <span className={classes.timeStamp}>
                          {m.isEdited ? "editada " : ""}
                          {formatTime(m.createdAt)}
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              {!messagesLoading && messages.length === 0 && (
                <Typography color="textSecondary">Sem mensagens.</Typography>
              )}
            </div>
          </>
        )}
      </Paper>
    </div>
  );
};

export default ChatsView;
